import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Form, Row, Button, Tooltip } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import moment from "moment";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { toDisplayFormat } from "../../lib/utils/formatDate";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import { TableRecord } from "../../lib/interfaces/TableRecord";

const IndividualSubscribersTable = ({ changeExiteIdOpenedModal }) => {
  const { accountSubscribers, accountSubscribersLoading } = useSelector(
    (state: RootStateOrAny) => state.accounts
  );

  const { subscribersVersions, subscribersVersionsLoading } = useSelector(
    (state: RootStateOrAny) => state.accountsTs.accountListRowData
  );

  const {
    accountId,
    fetchSubscriberVersions,
    handleCreateSubscriberVersion,
    handleCloseSubscriberVersion,
  } = useAccountInfoContainerContext();

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<TableRecord[]>([]);

  const [form] = Form.useForm();

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  useEffect(() => {
    if (checkIsLoaded(accountSubscribersLoading)) {
      setTableData(
        accountSubscribers
          .map((item) => ({
            key: item.id,
            id: item.id,
            externalId: item.externalId,
            fromDate: item.fromDate,
            toDate: item.toDate,
          }))
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [accountSubscribersLoading]);

  useEffect(() => {
    if (checkIsLoaded(subscribersVersionsLoading)) {
      const flattedSubscribersVersions = subscribersVersions.flat();
      const childData = flattedSubscribersVersions
        .map((version, i) => ({
          ...version,
          ...versionsTableUtils.getCommonChildTableProps(
            version,
            i,
            flattedSubscribersVersions.length
          ),
        }))
        .sort(versionsTableUtils.sortVersionsByDate);
      setChildTableData(childData);
    }
  }, [subscribersVersionsLoading]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    externalId: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Технический ID",
      dataIndex: "externalId",
      key: "externalId",
      width: columnsWidths.externalId,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Технический ID",
      dataIndex: "externalId",
      key: "externalId",
      width: columnsWidths.externalId,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="externalId"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          allowedFromDate={moment().startOf("month")}
          disabledToDate={(current) =>
            current.isBefore(record.fromDate, "day") ||
            current.isAfter(record.toDate, "day") ||
            current.isBefore(moment().startOf("month"), "day")
          }
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
  ]);

  const onFinish = ({ externalId, fromDate, toDate }) => {
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    if (changeableRecord.isCreated) {
      handleCreateSubscriberVersion(
        changeableRecord.id,
        fromDate,
        {
          accountId,
          externalId,
          id: changeableRecord.id,
          subscriberTypeId: changeableRecord.subscriberTypeId,
        }
        // ,
        // true
      );
    }
    if (changeableRecord.isClosing) {
      handleCloseSubscriberVersion(changeableRecord.id, toDate);
    }
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <ToggleableVersionsTable
        style={{ marginTop: "45px" }}
        title={
          <Row align="middle">
            <h4 style={{ margin: 0 }}>Технический ID</h4>
            <Tooltip title="Добавить номер телефона">
              <Button
                style={{ color: "#00BB77", marginLeft: "15px", border: "none" }}
                shape="circle"
                icon={<PlusCircleFilled />}
                onClick={changeExiteIdOpenedModal}
              />
            </Tooltip>
          </Row>
        }
        tableName="individual-subscribers-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={checkIsLoading(subscribersVersionsLoading)}
        tableClassName="individual-subscribers-table"
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={(record) => {
          fetchSubscriberVersions([record.id]);
        }}
      />
    </Form>
  );
};

export default IndividualSubscribersTable;
